import React, { Component } from 'react';
import { any, arrayOf, bool, func, shape } from 'prop-types';
import * as d3 from 'd3';
import { getMessage } from '@tcc/i18n';
import { container } from './css/BaseChart.css';
import styles from './css/HighLowTemperatures.css';
import { Help } from './Help';
import { QuantityUnitType } from '../../types';
import {
  generateChartConfig,
  formatGraphEntries,
  buildLine,
  drawLine,
  drawFocusDot,
  getWidthForLength,
  buildLocaleObject,
  getProjections,
} from '../../utils/chartUtils';

const buildLocaleTemperatureObject = (temperature) =>
  buildLocaleObject(temperature, 'temperature');

const showElements = (nodes) =>
  nodes.forEach((node) => node.style('display', null));
const hideElements = (nodes) =>
  nodes.forEach((node) => node.style('display', 'none'));
const hasScalar = (unitQtyTemp) => !Number.isNaN(Number(unitQtyTemp.scalar));

const checkForHistoricalData = ({ max, min }) => max.q.length || min.q.length;
export class HighLowTemperatures extends Component {
  containerWidth = 0;

  componentDidMount() {
    if (this.refContainer) {
      this.containerWidth = this.refContainer.clientWidth;
    }
    this.createLineGraph();
  }

  componentDidUpdate() {
    this.createLineGraph();
  }

  handleMousemove({
    element,
    tooltip,
    maxFocus,
    minFocus,
    yScale,
    max,
    min,
    hMax,
    hMin,
    hMaxFocus,
    hMinFocus,
    projectIndex,
  }) {
    const { formattedDates } = this.props;
    const localeGroup = 'temperature';
    const mouseIndex = d3.mouse(element)[0];
    const dataIndex = projectIndex(mouseIndex);
    const maxScalar = max.q[dataIndex];
    const minScalar = min.q[dataIndex];
    const unitQtyTempMax = formatGraphEntries(maxScalar, max.u, localeGroup);
    const unitQtyTempMin = formatGraphEntries(minScalar, min.u, localeGroup);
    const formattedTempMax = unitQtyTempMax.toString();
    const formattedTempMin = unitQtyTempMin.toString();

    const transformDots = (dotElement, scalar) =>
      dotElement.attr(
        'transform',
        `translate(${mouseIndex + 50},${yScale(scalar) - 7})`
      );

    if (hasScalar(unitQtyTempMax)) {
      transformDots(maxFocus, maxScalar);
    }

    if (hasScalar(unitQtyTempMin)) {
      transformDots(minFocus, minScalar);
    }
    const hasHistoricalData = checkForHistoricalData({ max: hMax, min: hMin });

    const unitQtyHistoricalTempMax =
      hasHistoricalData &&
      formatGraphEntries(hMax.q[dataIndex], hMax.u, localeGroup);

    const unitQtyHistoricalTempMin =
      hasHistoricalData &&
      formatGraphEntries(hMin.q[dataIndex], hMin.u, localeGroup);

    if (hasScalar(unitQtyHistoricalTempMax)) {
      transformDots(hMaxFocus, unitQtyHistoricalTempMax.scalar);
    }
    if (hasScalar(unitQtyHistoricalTempMax)) {
      transformDots(hMinFocus, unitQtyHistoricalTempMin.scalar);
    }

    tooltip
      .html(
        `
          <div>
            <div>${formattedDates[dataIndex]}</div>
            <div>${getMessage('chartTooltips.high')} ${formattedTempMax}</div>
            <div>${getMessage('chartTooltips.low')} ${formattedTempMin}</div>
          </div>
          ${
            hasHistoricalData
              ? `
            <div>
              <div>${getMessage('chartTooltips.yearsAveraged', {
                value: '',
              })}</div>
              <div>${getMessage(
                'chartTooltips.high'
              )} ${unitQtyHistoricalTempMax.toString()}</div>
              <div>${getMessage(
                'chartTooltips.low'
              )} ${unitQtyHistoricalTempMin.toString()}</div>
            </div>
          `
              : ''
          }
        <div class="arrow"></div>
      `
      )
      .style('left', `${mouseIndex + 13.5}px`)
      .style('top', `${yScale(maxScalar) - 7}px`);
  }

  createLineGraph = () => {
    const { endDate, startDate, temperature } = this.props;
    const {
      historical,
      past: { max, min },
    } = temperature;
    const hasHistoricalData = checkForHistoricalData(historical);
    const { node } = this;
    d3.select(node).selectAll('*').remove();

    const { u: maxUnit, q: maxQuantities } = buildLocaleTemperatureObject(max);
    const { u: minUnit, q: minQuantities } = buildLocaleTemperatureObject(min);
    const highest = d3.max(maxQuantities);
    const lowest = d3.min(minQuantities);
    const total = maxQuantities.length;

    const { height, width, yScale, timeDiff } = generateChartConfig(
      node,
      highest,
      lowest,
      total,
      startDate,
      endDate,
      this.containerWidth
    );
    const { projectIndex, projectPosition } = getProjections(width, timeDiff);

    const tempLine = buildLine(projectPosition, yScale);

    const g = d3.select(node).append('g').attr('class', styles.g);

    g.append(() => drawLine(maxQuantities, styles.tempMaxLine, tempLine));
    g.append(() => drawLine(minQuantities, styles.tempMinLine, tempLine));

    const maxFocus = g.append(() => drawFocusDot(styles.maxFocus));
    const minFocus = g.append(() => drawFocusDot(styles.minFocus));

    const tooltip = d3.selectAll(`.${styles.tooltip}`).style('display', 'none');

    const overlay = g
      .append('rect')
      .attr('class', styles.overlay)
      .attr('width', width)
      .attr('height', height);

    let elementsForOverlay = [tooltip, maxFocus, minFocus];

    let handleMousemoveArgs = {
      tooltip,
      yScale,
      maxFocus,
      minFocus,
      projectIndex,
      max: { q: maxQuantities, u: maxUnit },
      min: { q: minQuantities, u: minUnit },
      hMax: { q: [] },
      hMin: { q: [] },
    };

    if (hasHistoricalData) {
      const { max: hMax, min: hMin } = historical;

      const { u: hMaxUnit, q: hMaxQuantitie } =
        buildLocaleTemperatureObject(hMax);
      const { u: hMinUnit, q: hMinQuantitie } =
        buildLocaleTemperatureObject(hMin);

      const hMaxFocus = g.append(() => drawFocusDot(styles.historicalMaxFocus));
      const hMinFocus = g.append(() => drawFocusDot(styles.historicalMinFocus));

      g.append(() =>
        drawLine(hMaxQuantitie, styles.historicalTempMaxLine, tempLine)
      );
      g.append(() =>
        drawLine(hMinQuantitie, styles.historicalTempMinLine, tempLine)
      );

      elementsForOverlay = [...elementsForOverlay, hMaxFocus, hMinFocus];

      handleMousemoveArgs = {
        ...handleMousemoveArgs,
        hMax: { q: hMaxQuantitie, u: hMaxUnit },
        hMin: { q: hMinQuantitie, u: hMinUnit },
        hMaxFocus,
        hMinFocus,
      };
    }

    overlay
      .on('mouseover', () => {
        showElements(elementsForOverlay);
      })
      .on('mouseout', () => {
        hideElements(elementsForOverlay);
      });

    const handleMousemove = this.handleMousemove.bind(this);
    overlay.on('mousemove', function onMouseMove() {
      handleMousemove({
        element: this,
        ...handleMousemoveArgs,
      });
    });
  };

  render() {
    const { temperature, toggleHelp } = this.props;
    const {
      max: { q },
    } = temperature.past;
    const width = getWidthForLength(q.length, this.containerWidth) + 50;
    return (
      <div>
        <div className={styles.titleContainer}>
          <h2 className={styles.title}>{getMessage('past.tempTitle')}</h2>
          {/* TODO: Fix manual sentence construction */}
          <div
            className={styles.help}
            onClick={toggleHelp}
            role="button"
            tabIndex={0}
          >
            {getMessage('past.help')}
            <div>?</div>
          </div>
        </div>

        <Help
          showHelp={temperature.help}
          title={getMessage('past.tempHelp.header')}
          toggle={toggleHelp}
        >
          <li>{getMessage('past.tempHelp.p1')}</li>
          <li>{getMessage('past.tempHelp.p2')}</li>
        </Help>

        <div
          className={container}
          ref={(refContainer) => {
            this.refContainer = refContainer;
          }}
        >
          <svg
            className={styles.svg}
            data-test-id="high-low-temperatures-svg"
            height={300}
            ref={(node) => {
              this.node = node;
            }}
            width={width}
          />
          <div className={styles.tooltip} />
        </div>
      </div>
    );
  }
}

HighLowTemperatures.propTypes = {
  endDate: any,
  formattedDates: arrayOf(any),
  startDate: any,
  temperature: shape({
    past: QuantityUnitType,
    historical: QuantityUnitType,
    help: bool,
  }),
  toggleHelp: func.isRequired,
};

export default HighLowTemperatures;
